import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Input,
  Modal,
  Spinner,
  Text,
  VStack,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { authService, TwoFactorStatus } from "services/authService";
import { getThemeColor } from "theme/colors";
import TwoFactorSetupWizard from "./TwoFactorSetupWizard";

/**
 * Profile "Security" card: shows the user's 2FA state and lets them enable, re-sync, or
 * disable it. Disable is hidden when the company mandates 2FA.
 */
export default function ProfileTwoFactorSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [disableError, setDisableError] = useState("");

  const reload = async () => {
    try {
      setLoading(true);
      setStatus(await authService.get2faStatus());
    } catch {
      // Leave status null; the card still offers an enable affordance.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleSetupComplete = () => {
    setSetupOpen(false);
    notify({ type: "success", title: t("app.twoFactor.enabledToast") });
    reload();
  };

  const handleDisable = async () => {
    setDisableError("");
    const code = disableCode.trim();
    if (code.length !== 6) {
      setDisableError(t("app.twoFactor.invalidCodeFormat"));
      return;
    }
    setDisabling(true);
    try {
      await authService.disable2fa(code);
      setDisableOpen(false);
      setDisableCode("");
      notify({ type: "success", title: t("app.twoFactor.disabledToast") });
      reload();
    } catch (err: any) {
      setDisableError(
        err.response?.data?.message || t("app.twoFactor.verifyFailed"),
      );
    } finally {
      setDisabling(false);
    }
  };

  const enabled = status?.enabled ?? false;
  const companyRequires = status?.companyRequires ?? false;

  return (
    <Card.Root>
      <Card.Body p={6}>
        <Heading
          size="xl"
          fontWeight="bold"
          color={getThemeColor("text.primary")}
          mb={2}
        >
          {t("app.twoFactor.sectionTitle")}
        </Heading>
        <Text fontSize="sm" color={getThemeColor("text.secondary")} mb={4}>
          {t("app.twoFactor.sectionDescription")}
        </Text>

        {loading ? (
          <Spinner />
        ) : (
          <Flex justify="space-between" align="center" gap={4} wrap="wrap">
            <Box>
              <Text fontWeight="semibold" color={getThemeColor("text.primary")}>
                {enabled
                  ? t("app.twoFactor.statusEnabled")
                  : t("app.twoFactor.statusDisabled")}
              </Text>
              {companyRequires && (
                <Text fontSize="xs" color={getThemeColor("text.tertiary")}>
                  {t("app.twoFactor.companyRequiresNote")}
                </Text>
              )}
            </Box>
            <Flex gap={3}>
              {enabled ? (
                <>
                  <Button intent="secondary" onClick={() => setSetupOpen(true)}>
                    {t("app.twoFactor.resync")}
                  </Button>
                  {!companyRequires && (
                    <Button
                      intent="danger"
                      onClick={() => setDisableOpen(true)}
                    >
                      {t("app.twoFactor.disableButton")}
                    </Button>
                  )}
                </>
              ) : (
                <Button intent="primary" onClick={() => setSetupOpen(true)}>
                  {t("app.twoFactor.enableButton")}
                </Button>
              )}
            </Flex>
          </Flex>
        )}
      </Card.Body>

      {/* Enroll / re-sync wizard */}
      <Modal isOpen={setupOpen} onClose={() => setSetupOpen(false)} size="md">
        <Modal.Header>{t("app.twoFactor.setup.title")}</Modal.Header>
        <Modal.Body pb={6}>
          {setupOpen && (
            <TwoFactorSetupWizard
              onStart={() => authService.setup2fa()}
              onVerify={(code) => authService.enable2fa(code)}
              onComplete={handleSetupComplete}
              onCancel={() => setSetupOpen(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Disable prompt (requires a current code) */}
      <Modal
        isOpen={disableOpen}
        onClose={() => setDisableOpen(false)}
        size="sm"
      >
        <Modal.Header>{t("app.twoFactor.disable.title")}</Modal.Header>
        <Modal.Body>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color={getThemeColor("text.secondary")}>
              {t("app.twoFactor.disable.instruction")}
            </Text>
            <Input
              id="disable2faCode"
              label={t("app.twoFactor.codeLabel")}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              value={disableCode}
              onChange={(e) =>
                setDisableCode(e.target.value.replace(/\D/g, ""))
              }
            />
            {disableError && (
              <Text fontSize="sm" color={getThemeColor("alert.error.text")}>
                {disableError}
              </Text>
            )}
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <Button intent="secondary" onClick={() => setDisableOpen(false)}>
            {t("app.common.cancel")}
          </Button>
          <Button intent="danger" onClick={handleDisable} disabled={disabling}>
            {disabling
              ? t("app.twoFactor.disabling")
              : t("app.twoFactor.disableButton")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card.Root>
  );
}
